import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Tag, ArrowRight } from "lucide-react";
import { format } from "date-fns";

interface Blog {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  featured_image_url: string | null;
  status: string;
  published_at: string | null;
  author_name: string | null;
  read_time_minutes: number | null;
  views_count: number;
  tags: string[] | null;
  category: string | null;
  created_at: string;
}

interface LandingBlogsSectionProps {
  locationId: string;
  theme: any;
  content?: any;
  isStandalonePage?: boolean;
  ssrBlogsData?: any[] | null;
}

const LandingBlogsSection = ({ locationId, theme, content, isStandalonePage = false, ssrBlogsData }: LandingBlogsSectionProps) => {
  const { subdomain } = useParams<{ subdomain: string }>();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState<Blog[]>(ssrBlogsData || []);
  const [loading, setLoading] = useState(!ssrBlogsData);

  // Check if we're on an actual subdomain (subdomain.localhost) vs testing route (/subdomain/:subdomain)
  const isOnSubdomain = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      // subdomain.localhost or subdomain.my-domain.com
      return parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www';
    }
    return false;
  };

  const getBasePath = () => {
    return isOnSubdomain() ? '' : `/subdomain/${subdomain}`;
  };

  useEffect(() => {
    // If we have SSR data, don't fetch again
    if (ssrBlogsData) {
      return;
    }

    fetchBlogs();
  }, [locationId, isStandalonePage, ssrBlogsData]);

  const fetchBlogs = async () => {
    try {
      console.log("Fetching blogs for location:", locationId);

      const { data: blogLocations, error: blogLocationsError } = await supabase
        .from("blog_locations")
        .select("blog_id")
        .eq("location_id", locationId);

      if (blogLocationsError) {
        console.error("Error fetching blog locations:", blogLocationsError);
        setLoading(false);
        return;
      }

      if (!blogLocations || blogLocations.length === 0) {
        console.log("No blogs found for this location");
        setBlogs([]);
        setLoading(false);
        return;
      }

      const blogIds = blogLocations.map((bl) => bl.blog_id);
      const limit = isStandalonePage ? 12 : 6;

      const { data: blogsData, error: blogsError } = await supabase
        .from("blogs")
        .select("*")
        .in("id", blogIds)
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(limit);

      if (blogsError) {
        console.error("Error fetching blogs:", blogsError);
        setLoading(false);
        return;
      }

      console.log("Fetched blogs:", blogsData);
      if (blogsData && blogsData.length > 0) {
        console.log("First blog slug:", blogsData[0].slug);
        console.log("First blog title:", blogsData[0].title);
      }
      setBlogs(blogsData || []);
    } catch (error) {
      console.error("Error in fetchBlogs:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "dd MMM yy");
    } catch (error) {
      return "";
    }
  };

  const stripHtml = (html: string) => {
    // Server-safe HTML stripping using regex
    if (typeof window === 'undefined') {
      // Server-side: use regex to strip HTML tags
      return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    }
    // Client-side: use DOM API
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + "...";
  };

  if (loading) {
    return (
      <section id="blogs" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: "var(--primary)" }}
            >
              {content?.heading || "Latest from Our Blog"}
            </h2>
            <div className="w-24 h-1 mx-auto" style={{ backgroundColor: "var(--primary)" }} />
          </div>
          <div className="text-center text-gray-500">Loading blogs...</div>
        </div>
      </section>
    );
  }

  if (!blogs || blogs.length === 0) {
    return null;
  }

  return (
    <section id="blogs" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: "var(--primary)" }}
          >
            {content?.heading || "Latest from Our Blog"}
          </h2>
          <div className="w-24 h-1 mx-auto" style={{ backgroundColor: "var(--primary)" }} />
          <p className="mt-6 text-lg text-gray-600 max-w-2xl mx-auto">
            {content?.description || "Stay updated with our latest insights, news, and articles"}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map((blog) => (
            <Card key={blog.id} className="blog-card overflow-hidden hover:shadow-lg transition-shadow">
              {blog.featured_image_url && (
                <div className="h-48 overflow-hidden">
                  <img
                    src={blog.featured_image_url}
                    alt={blog.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardContent className="p-6">
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  {blog.published_at && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{formatDate(blog.published_at)}</span>
                    </div>
                  )}
                  {blog.read_time_minutes && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{blog.read_time_minutes} min read</span>
                    </div>
                  )}
                </div>

                {blog.category && (
                  <div className="mb-2">
                    <span
                      className="text-xs font-semibold px-3 py-1 rounded-full"
                      style={{
                        backgroundColor: "color-mix(in srgb, var(--primary) 20%, transparent)",
                        color: "var(--primary)",
                      }}
                    >
                      {blog.category}
                    </span>
                  </div>
                )}

                <h3 className="text-xl font-bold mb-3 line-clamp-2">{blog.title}</h3>

                <p className="text-gray-600 mb-4 line-clamp-3">
                  {blog.excerpt || truncateText(stripHtml(blog.content), 150)}
                </p>

                {blog.tags && blog.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {blog.tags.slice(0, 3).map((tag, index) => (
                      <div key={index} className="flex items-center gap-1 text-xs text-gray-500">
                        <Tag className="h-3 w-3" />
                        <span>{tag}</span>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  variant="link"
                  className="p-0 h-auto"
                  style={{ color: "var(--primary)" }}
                  onClick={() => {
                    navigate(`${getBasePath()}/blog/${blog.slug}`);
                  }}
                >
                  Read More <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {!isStandalonePage && (
          <div className="text-center mt-12">
            <Button
              onClick={() => navigate(`${getBasePath()}/blogs`)}
              style={{ backgroundColor: "var(--primary)", color: "var(--primary-foreground)" }}
              className="hover:opacity-90"
            >
              Show More Blogs
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default LandingBlogsSection;